import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { companiesApi, SubmittedPage } from '../../api';
import PageInfoButton from '@/components/PageInfoButton';



const CompanyFrameworkReview: React.FC = () => {
  const { frameworkId } = useParams<{ frameworkId: string }>();
  const [submittedPages, setSubmittedPages] = useState<SubmittedPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState<SubmittedPage | null>(null);
  const [reviewComment, setReviewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (frameworkId) {
      loadSubmittedPages();
    }
  }, [frameworkId]);

  const loadSubmittedPages = async () => {
    if (!frameworkId) return;

    try {
      setLoading(true);
      const data = await companiesApi.getSubmittedPagesForFramework(parseInt(frameworkId));
      setSubmittedPages(data);
    } catch (error) {
      console.error('Failed to load submitted pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (pageId: number) => {
    try {
      setSubmitting(true);
      await companiesApi.approvePageForAudit(pageId);

      // Remove from submitted list
      setSubmittedPages(pages => pages.filter(p => p.id !== pageId));
      setSelectedPage(null);
      setReviewComment('');
    } catch (error) {
      console.error('Failed to approve page:', error);
      alert('Failed to approve page. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestChanges = async (pageId: number) => {
    if (!reviewComment.trim()) {
      alert('Please provide a comment explaining what changes are needed.');
      return;
    }

    try {
      setSubmitting(true);
      await companiesApi.requestChanges(pageId, reviewComment.trim());

      // Remove from submitted list
      setSubmittedPages(pages => pages.filter(p => p.id !== pageId));
      setSelectedPage(null);
      setReviewComment('');
    } catch (error) {
      console.error('Failed to request changes:', error);
      alert('Failed to request changes. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Framework Review</h1><PageInfoButton /></div>
            <p className="text-slate-600 mt-1">
              Review submitted compliance pages for approval
            </p>
          </div>
          <Link
            to="/company/dashboard"
            className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
          >
            ← Back to Dashboard
          </Link>
        </div>
      </div>

      {/* Submitted Pages List */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900">
            Submitted Pages ({submittedPages.length})
          </h2>
        </div>

        <div className="p-6">
          {submittedPages.length === 0 ? (
            <div className="py-12">
              <div className="text-slate-400 text-6xl mb-4">📋</div>
              <h3 className="text-lg font-medium text-slate-900 mb-2">No pages submitted for review</h3>
              <p className="text-slate-600">Compliance users haven't submitted any pages yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {submittedPages.map(page => (
                <div key={page.id} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-slate-900">{page.title}</h3>
                      <p className="text-sm text-slate-600 mt-1">{page.description}</p>
                      <div className="flex items-center space-x-4 mt-2 text-sm text-slate-500">
                        <span>Submitted by {page.first_name} {page.last_name}</span>
                        <span>{page.submitted_at ? new Date(page.submitted_at).toLocaleDateString() : 'Unknown'}</span>
                        <span>{page.evidence_files ? page.evidence_files.split(';').length : 0} evidence files</span>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setSelectedPage(page)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Review
                      </button>
                      <button
                        onClick={() => handleApprove(page.id)}
                        disabled={submitting}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                      >
                        Quick Approve
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Review Modal */}
      {selectedPage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-100">
              <h2 className="text-xl font-semibold text-slate-900">Review: {selectedPage.title}</h2>
              <p className="text-slate-600 mt-1">{selectedPage.description}</p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Review Comments (required for change requests)
                </label>
                <textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  placeholder="Explain what changes are needed or provide approval feedback..."
                  className="w-full h-32 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setSelectedPage(null);
                  setReviewComment('');
                }}
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRequestChanges(selectedPage.id)}
                disabled={submitting || !reviewComment.trim()}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50"
              >
                Request Changes
              </button>
              <button
                onClick={() => handleApprove(selectedPage.id)}
                disabled={submitting}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyFrameworkReview;